// Public Expert Profile Page
// SEO-optimized expert profile for AI search engine indexing
// Route: /#/expert/:slug (hash-based routing)

import React, { useEffect, useState } from 'react';
import { Award, MapPin, Building2, TrendingUp, Users, ExternalLink, Share2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface ExpertProfile {
  slug: string;
  name: string;
  photo: string | null;
  company: string | null;
  bio: string | null;
  location: string | null;
  level: string;
  levelDescription: string;
  expertise: {
    primary: string | null;
    secondary: string[];
    allTopics: string[];
    tools: string[];
    industries: string[];
  };
  stats: {
    totalConversations: number;
    conversationsLast30Days: number;
    memberSince: string | null;
    activeReferrals: number;
  };
  aiVisibility: {
    enabled: boolean;
    searchRanking: number;
  };
  referral: {
    available: boolean;
    joinUrl: string | null;
  };
  profileUrl: string;
  structuredData: any;
}

interface PublicExpertProfileProps {
  slug: string;
}

const PublicExpertProfile: React.FC<PublicExpertProfileProps> = ({ slug }) => {
  const [profile, setProfile] = useState<ExpertProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadProfile(slug);
    }
  }, [slug]);

  const loadProfile = async (expertSlug: string) => {
    try {
      // Call Firebase Function endpoint
      const response = await fetch(
        `https://europe-west1-superkonnectedwebsite.cloudfunctions.net/getExpertBySlug/${expertSlug}`
      );
      
      if (!response.ok) {
        if (response.status === 404) {
          setError('Expert niet gevonden');
        } else {
          setError('Fout bij laden profiel');
        }
        return;
      }
      
      const data = await response.json();
      setProfile(data);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Kon profiel niet laden');
    } finally {
      setLoading(false);
    }
  };

  const shareProfile = async () => {
    if (!profile) return;
    
    const shareData = {
      title: `${profile.name} - ${profile.levelDescription}`,
      text: `Bekijk het expert profiel van ${profile.name} op SuperKonnected`,
      url: profile.profileUrl
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      navigator.clipboard.writeText(profile.profileUrl);
      alert('Link gekopieerd!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Profiel laden...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Expert niet gevonden</h1>
          <p className="text-gray-600 mb-4">{error || 'Dit profiel bestaat niet of is niet publiek.'}</p>
          <button
            onClick={() => { window.location.hash = '#dashboard'; }}
            className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Terug naar home
          </button>
        </div>
      </div>
    );
  }

  const levelColors: Record<string, string> = {
    verified: 'from-blue-500 to-blue-600',
    expert: 'from-purple-500 to-purple-600',
    top_expert: 'from-amber-500 to-amber-600'
  };

  const levelBgColors: Record<string, string> = {
    verified: 'bg-blue-50',
    expert: 'bg-purple-50',
    top_expert: 'bg-amber-50'
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{profile.name} - {profile.levelDescription} | SuperKonnected</title>
        <meta name="description" content={profile.bio || `${profile.name} is een ${profile.levelDescription.toLowerCase()} op SuperKonnected. Expertise in ${profile.expertise.primary || 'ondernemerschap'}.`} />
        <link rel="canonical" href={profile.profileUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${profile.name} - ${profile.levelDescription}`} />
        <meta property="og:description" content={profile.bio || `Expert profiel van ${profile.name}`} />
        <meta property="og:image" content={profile.photo || 'https://community.superkonnected.nl/default-avatar.png'} />
        <meta property="og:url" content={profile.profileUrl} />
        <meta property="og:type" content="profile" />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(profile.structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header / Hero Section */}
        <div className={`${levelBgColors[profile.level] || 'bg-gray-100'} border-b border-gray-200`}>
          <div className="max-w-4xl mx-auto px-4 py-12">
            <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
              {/* Profile Photo */}
              <div className="relative">
                {profile.photo ? (
                  <img
                    src={profile.photo}
                    alt={profile.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-orange-400 to-blue-500 flex items-center justify-center border-4 border-white shadow-lg">
                    <span className="text-4xl font-bold text-white">
                      {profile.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                
                {/* Expert Badge */}
                <div className={`absolute -bottom-2 -right-2 bg-gradient-to-r ${levelColors[profile.level] || 'from-gray-400 to-gray-500'} text-white px-3 py-1 rounded-full flex items-center space-x-1 shadow-lg`}>
                  <Award size={14} />
                  <span className="text-xs font-bold">{profile.levelDescription}</span>
                </div>
              </div>

              {/* Profile Info */}
              <div className="flex-1 text-center md:text-left">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {profile.name}
                </h1>
                
                {profile.company && (
                  <div className="flex items-center justify-center md:justify-start space-x-2 text-gray-700 mb-2">
                    <Building2 size={18} />
                    <span className="font-medium">{profile.company}</span>
                  </div>
                )}
                
                {profile.location && (
                  <div className="flex items-center justify-center md:justify-start space-x-2 text-gray-600 mb-4">
                    <MapPin size={16} />
                    <span className="text-sm">{profile.location}</span>
                  </div>
                )}
                
                {profile.bio && (
                  <p className="text-gray-700 leading-relaxed max-w-2xl">
                    {profile.bio}
                  </p>
                )}

                {/* AI Visibility Badge */}
                {profile.aiVisibility.enabled && (
                  <div className="inline-flex items-center space-x-2 mt-4 px-3 py-1 bg-white border border-emerald-200 rounded-full">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-emerald-700">Vindbaar via AI</span>
                  </div>
                )}
              </div>

              {/* Share Button */}
              <button
                onClick={shareProfile}
                className="p-3 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors shadow-sm"
                title="Deel profiel"
              >
                <Share2 size={20} className="text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">{profile.stats.totalConversations}</p>
              <p className="text-sm text-gray-600">Gesprekken</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">{profile.stats.conversationsLast30Days}</p>
              <p className="text-sm text-gray-600">Deze maand</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">
                {profile.stats.memberSince ? new Date(profile.stats.memberSince).getFullYear() : '-'}
              </p>
              <p className="text-sm text-gray-600">Lid sinds</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">{profile.stats.activeReferrals}</p>
              <p className="text-sm text-gray-600">Verwijzingen</p>
            </div>
          </div>

          {/* Expertise */}
          {profile.expertise.primary && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="text-orange-500" size={20} />
                <h2 className="text-xl font-bold text-gray-800">Expertise</h2>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Primaire focus</p>
                <div className="inline-block px-4 py-2 bg-gradient-to-r from-orange-100 to-blue-100 border border-orange-200 rounded-lg">
                  <span className="font-semibold text-gray-800">{profile.expertise.primary}</span>
                </div>
              </div>

              {profile.expertise.secondary.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Ook actief in</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.expertise.secondary.map((topic, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full border border-blue-200">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile.expertise.tools.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Tools & Methodes</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.expertise.tools.map((tool, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full border border-purple-200">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile.expertise.industries.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Industrieën</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.expertise.industries.map((industry, idx) => (
                      <span key={idx} className="px-3 py-1 bg-emerald-50 text-emerald-700 text-sm rounded-full border border-emerald-200">
                        {industry}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* CTA - Join via Referral */}
          {profile.referral.available && profile.referral.joinUrl && (
            <div className="bg-gradient-to-r from-orange-500 to-blue-500 rounded-lg p-8 text-center text-white mb-6">
              <Users size={48} className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Word lid via {profile.name.split(' ')[0]}</h2>
              <p className="mb-6 opacity-90">
                Start je reis in de SuperKonnected Community met een persoonlijke uitnodiging
              </p>
              <a
                href={profile.referral.joinUrl}
                className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-orange-600 rounded-lg font-bold hover:bg-gray-50 transition-colors shadow-lg"
              >
                <span>Word lid via deze expert</span>
                <ExternalLink size={18} />
              </a>
            </div>
          )}

          {/* About SuperKonnected */}
          <div className="bg-gray-100 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-600 mb-4">
              {profile.name} is onderdeel van de SuperKonnected Community - een professioneel netwerk voor ondernemers en podcasters.
            </p>
            <a
              href="https://www.superkonnected.nl"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-orange-600 hover:text-orange-700 font-medium"
            >
              <span>Meer over SuperKonnected</span>
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicExpertProfile;
